import React, { useCallback, useEffect, useMemo, useState } from "react";
import { InputAttributes, NumberFormatPropsBase } from "react-number-format";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Autocomplete, IconButton, Stack, TextField, TextFieldProps } from "@mui/material";
import { BinDeleteIcon, OverrideIcon } from "assets/material-custom-icons";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { debounce } from "lodash";
import { useDealStatusesSelector } from "store";

import { Characteristic, useForm } from ".";
import { currenciesArray, RoleName } from "../../../constants";
import { CharAutocomplete } from "./CharAutocomplete";
import { DatePicker } from "./DatePicker";
import { ListAutocomplete } from "./ListAutocomplete";

interface Props {
  characteristic: Characteristic;
  viewOnly: boolean;
  subcategoryId?: number;
  hideEyeIcon?: boolean;
}

const currencyCharVarName = "Currency";

const Inputs: Record<
  string,
  React.FC<TextFieldProps & { numberInputProps?: NumberFormatPropsBase<InputAttributes> }>
> = {
  number: NumberInput,
  percentage: NumberInput,
  currency: NumberInput,
  new_text: TextField,
  new_number: NumberInput,
  new_percentage: NumberInput,
  new_currency: NumberInput
};

export const SingleData: React.FC<Props> = ({ characteristic, viewOnly, hideEyeIcon, subcategoryId }) => {
  const dealStatuses = useDealStatusesSelector();
  const { is_show, name, id, data_type, is_global, required_to_status_ids } = characteristic;

  const hasRestrictions = !!required_to_status_ids?.length;
  const requiredStagesTooltip = hasRestrictions
    ? `This field is required to change the deal status to: ${required_to_status_ids
        .map((statusId) => dealStatuses.find((s) => s.id === statusId)?.name ?? "")
        .filter(Boolean)
        .join(", ")}`
    : "";

  const {
    getOneData,
    updateCharData,
    updateChar,
    deleteChar,
    setCharacteristicRef,
    viewBy,
    overrideCharacteristicData,
    isOverrideAvailable,
    dealId,
    missingFieldNames,
    missingFieldRefsMap
  } = useForm();

  const isMissingField =
    Boolean(missingFieldNames?.length) && missingFieldNames!.some((m) => m.trim().toLowerCase() === name.toLowerCase());

  const {
    value = "",
    id: dataId,
    currency
  } = getOneData({
    characteristic_id: id,
    investor_id: viewBy?.investor?.id || null,
    round_id: viewBy?.round?.id || null
  }) || {};

  const [localValue, setLocalValue] = useState<string>(value);

  // sync local value with value after viewBy change
  useEffect(() => {
    setLocalValue(value);
  }, [value, viewBy]);

  // Common input components
  const Input = typeof id === "number" ? Inputs[data_type] : Inputs[`new_${data_type}`];

  const isAutocomplete: boolean = typeof id === "number" && data_type === "text";
  const isDate: boolean = data_type === "date";
  const isList: boolean = data_type === "list";

  const debounceUpdate = useMemo(() => debounce(updateCharData, 300), [updateCharData]);

  const handleValueChange = (value: string) => {
    setLocalValue(value);

    debounceUpdate({
      id: dataId,
      value,
      characteristic_id: id,
      investor_id: viewBy?.investor?.id || null,
      round_id: viewBy?.round?.id || null,
      ...(!value && typeof dataId === "number" && { is_deleted: true })
    });
  };

  const isCurrencyDefault = data_type === "currency" && name === currencyCharVarName;

  const isRequiredForStages = !!required_to_status_ids?.length;

  const checkIsOverrideAvailable = useCallback(() => {
    return isOverrideAvailable({
      dealOverrideId: !viewBy ? dealId : undefined,
      roundOverrideId: viewBy?.round?.id || undefined,
      characteristicId: id
    });
  }, [isOverrideAvailable, dealId, viewBy, id]);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (el) setCharacteristicRef(el, id);
      if (missingFieldRefsMap && isMissingField) {
        const key = name.trim().toLowerCase();
        if (el) {
          missingFieldRefsMap.current[key] = el;
        } else {
          delete missingFieldRefsMap.current[key];
        }
      }
    },
    [setCharacteristicRef, id, missingFieldRefsMap, name, missingFieldNames]
  );

  return (
    <Stack direction="row" alignItems="center" gap="10px" ref={setRef}>
      <Stack
        direction="row"
        alignItems="center"
        gap="10px"
        sx={{
          position: "relative",
          flexGrow: 1,
          minWidth: 0,
          ...(isMissingField && {
            backgroundColor: "rgba(255, 235, 59, 0.35)",
            borderRadius: 1
          })
        }}
      >
        {hasRestrictions && (
          <MaterialTooltip title={requiredStagesTooltip} isShow>
            <IconButton
              size="sm"
              sx={{
                position: "absolute",
                top: -11,
                right: -11,
                zIndex: 1,
                padding: "3px",
                color: "lightgray",
                backgroundColor: "white",
                border: 0,
                "&.MuiIconButton-root:hover": {
                  backgroundColor: "white",
                  color: "lightgray"
                }
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </MaterialTooltip>
        )}
        {isCurrencyDefault && (
          //default "Currency" characteristic
          <Autocomplete
            fullWidth
            options={currenciesArray}
            value={value || ""}
            onChange={(_, option) => {
              handleValueChange(option);
            }}
            disableClearable
            disabled={viewOnly}
            renderInput={(params) => <TextField {...params} label="Currency" />}
          />
        )}
        {Input && !isCurrencyDefault && (
          <Input
            fullWidth
            label={name}
            required={isRequiredForStages}
            size="small"
            value={localValue}
            onChange={(e) => handleValueChange(e.target.value)}
            disabled={viewOnly}
            {...(data_type === "percentage" && { numberInputProps: { suffix: "%", decimalScale: 2 } })}
          />
        )}
        {isAutocomplete && (
          <CharAutocomplete
            value={localValue}
            onChange={handleValueChange}
            characteristicId={id as number}
            charName={name}
            disabled={viewOnly}
          />
        )}
        {isList && characteristic.list_options && subcategoryId && (
          <ListAutocomplete
            options={
              Array.isArray(characteristic.list_options)
                ? characteristic.list_options
                : characteristic.list_options[subcategoryId]
            }
            onChange={handleValueChange}
            charName={name}
            value={localValue}
            disabled={viewOnly}
          />
        )}
        {isList && characteristic.list_options && Array.isArray(characteristic.list_options) && (
          <ListAutocomplete
            options={characteristic.list_options}
            onChange={handleValueChange}
            charName={name}
            value={localValue}
            disabled={viewOnly}
          />
        )}
        {isDate && (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ width: "100%" }}>
            <DatePicker
              label={name}
              value={localValue}
              onChange={handleValueChange}
              onClear={() => handleValueChange("")}
              disabled={viewOnly}
            />
            {isRequiredForStages && (
              <MaterialTooltip title={requiredStagesTooltip} isShow>
                <span style={{ color: "#d32f2f", fontWeight: "bold", marginTop: "8px" }}>*</span>
              </MaterialTooltip>
            )}
          </Stack>
        )}
        {!viewOnly && data_type === "currency" && name !== currencyCharVarName && (
          // characteristic with currency type
          <Autocomplete
            options={currenciesArray}
            value={currency || ""}
            onChange={(_, option) => {
              updateCharData({
                id: dataId,
                characteristic_id: id,
                currency: option,
                investor_id: viewBy?.investor?.id || null,
                round_id: viewBy?.round?.id || null
              });
            }}
            disableClearable
            disabled={viewOnly}
            renderInput={(params) => <TextField sx={{ width: "100px" }} {...params} label="Currency" />}
          />
        )}
      </Stack>
      <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={hideEyeIcon}>
        <Stack direction="row" alignItems="center">
          {checkIsOverrideAvailable() && !viewOnly && !viewBy?.investor?.id && value && (
            <MaterialTooltip title={!viewBy ? "Override all with Deal Value" : "Override all with Round Value"} isShow>
              <IconButton
                onClick={(e) =>
                  dealId &&
                  overrideCharacteristicData({
                    dealId,
                    dealOverrideId: !viewBy ? dealId : undefined,
                    roundOverrideId: viewBy?.round?.id || undefined,
                    characteristicId: id,
                    anchorEl: e.currentTarget,
                    characteristicName: name
                  })
                }
              >
                <OverrideIcon />
              </IconButton>
            </MaterialTooltip>
          )}

          {!(is_global || viewOnly || Boolean(viewBy)) && (
            <IconButton onClick={() => deleteChar(characteristic)}>
              <BinDeleteIcon />
            </IconButton>
          )}
          <IconButton disabled={viewOnly || Boolean(viewBy)} onClick={() => updateChar({ id, is_show: !is_show })}>
            {is_show ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon sx={{ color: "#92A7BC" }} />}
          </IconButton>
        </Stack>
      </RoleComponentGuard>
    </Stack>
  );
};
